import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../../../../services/api";
import {
  Modal,
  Button,
  Table,
  Badge,
  Spinner,
  Alert,
  Form,
  InputGroup,
  Pagination,
} from "react-bootstrap";
import { FaEye, FaCheckCircle, FaSearch } from "react-icons/fa";
import { toast } from "react-toastify";
import AssignmentService from "../../../../services/assignmentService";

const AssignmentSubmissionsView = ({ show, onHide, assignment }) => {
  const [submissions, setSubmissions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [obtainedMarks, setObtainedMarks] = useState("");
  const [feedback, setFeedback] = useState("");
  const [gradingLoading, setGradingLoading] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1,
  });
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    if (show && assignment) {
      loadSubmissions();
    }
  }, [show, assignment, pagination.current_page, itemsPerPage, searchTerm]);

  const loadSubmissions = async () => {
    if (!assignment) return;

    try {
      setLoading(true);
      const response = await AssignmentService.getSubmissions(
        assignment.id,
        pagination.current_page,
        itemsPerPage,
        searchTerm,
      );

      if (response.data) {
        setSubmissions(response.data.submissions || []);
        setSummary(response.data.summary || null);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }
    } catch (error) {
      console.error("Failed to load submissions:", error);
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  const handleViewSubmission = (submission) => {
    setSelectedSubmission(submission);
    setObtainedMarks(submission.obtained_marks || "");
    setFeedback(submission.feedback || "");
    setShowGradeModal(true);
  };

  const handleGradeSubmit = async () => {
    if (!selectedSubmission) return;

    const marks = parseInt(obtainedMarks);
    if (isNaN(marks) || marks < 0 || marks > (assignment?.total_marks || 100)) {
      toast.error(
        `Marks must be between 0 and ${assignment?.total_marks || 100}`,
      );
      return;
    }

    try {
      setGradingLoading(true);
      await AssignmentService.gradeSubmission(
        selectedSubmission.id,
        marks,
        feedback,
      );
      toast.success("Submission graded successfully");
      setShowGradeModal(false);
      setSelectedSubmission(null);
      loadSubmissions();
    } catch (error) {
      console.error("Failed to grade submission:", error);
      toast.error("Failed to grade submission");
    } finally {
      setGradingLoading(false);
    }
  };

  const viewFile = (filePath) => {
    const url = `${API_BASE_URL}/${filePath}`;
    window.open(url, "_blank");
  };

  const downloadFile = (filePath, fileName) => {
    const url = `${API_BASE_URL}/${filePath}`;
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "submission";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const getStatusBadge = (submission) => {
    if (submission.obtained_marks !== null) {
      return <Badge bg="success">Graded</Badge>;
    }
    if (submission.status === "late") {
      return <Badge bg="danger">Late</Badge>;
    }
    return <Badge bg="warning">Submitted</Badge>;
  };

  const formatDate = (date) => {
    if (!date) return "Not submitted";
    try {
      return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return date;
    }
  };

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.total_pages) {
      setPagination((prev) => ({ ...prev, current_page: pageNumber }));
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const renderPagination = () => {
    const { current_page, total_pages } = pagination;
    if (total_pages <= 1) return null;

    let items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, current_page - Math.floor(maxVisible / 2));
    let endPage = Math.min(total_pages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(current_page - 1)}
        disabled={current_page === 1}
      />,
    );

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>,
      );
      if (startPage > 2)
        items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === current_page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>,
      );
    }

    if (endPage < total_pages) {
      if (endPage < total_pages - 1)
        items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
      items.push(
        <Pagination.Item
          key={total_pages}
          onClick={() => handlePageChange(total_pages)}
        >
          {total_pages}
        </Pagination.Item>,
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(current_page + 1)}
        disabled={current_page === total_pages}
      />,
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="d-flex align-items-center gap-2">
          <Form.Select
            size="sm"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            style={{ width: "auto" }}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </Form.Select>
        </div>
        <Pagination size="sm">{items}</Pagination>
      </div>
    );
  };

  if (!assignment) return null;

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Submissions: {assignment.title}
            <div className="small text-muted mt-1">
              Due: {formatDate(assignment.due_date)} | Total Marks:{" "}
              {assignment.total_marks}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Summary Cards */}
          {summary && (
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card bg-info text-white">
                  <div className="card-body text-center py-2">
                    <h5>{summary.total_students || 0}</h5>
                    <small>Total Students</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-success text-white">
                  <div className="card-body text-center py-2">
                    <h5>{summary.submitted_count || 0}</h5>
                    <small>Submitted</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-warning text-white">
                  <div className="card-body text-center py-2">
                    <h5>{summary.pending_count || 0}</h5>
                    <small>Pending</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-primary text-white">
                  <div className="card-body text-center py-2">
                    <h5>{summary.graded_count || 0}</h5>
                    <small>Graded</small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Search Bar */}
          <InputGroup className="mb-3" style={{ width: "300px" }}>
            <Form.Control
              placeholder="Search by student name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            />
            <Button variant="outline-primary" onClick={handleSearch}>
              <FaSearch />
            </Button>
          </InputGroup>

          {/* Submissions Table */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading submissions...</p>
            </div>
          ) : submissions.length === 0 ? (
            <Alert variant="info" className="text-center">
              No submissions yet.
            </Alert>
          ) : (
            <>
              <Table striped hover responsive>
                <thead>
                  <tr>
                    <th>Student Name</th>
                    <th>Submitted At</th>
                    <th>Submission</th>
                    <th>Status</th>
                    <th>Marks</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((sub) => (
                    <tr key={sub.id}>
                      <td>
                        <strong>{sub.student_name}</strong>
                        <div className="small text-muted">
                          {sub.student_email}
                        </div>
                      </td>
                      <td>
                        {sub.submitted_at ? formatDate(sub.submitted_at) : "-"}
                      </td>
                      <td>
                        {sub.submission_text && (
                          <div className="mb-1">
                            <small className="text-muted">
                              {sub.submission_text.substring(0, 50)}...
                            </small>
                          </div>
                        )}
                        {sub.attachment_path && (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 me-2"
                            onClick={() => viewFile(sub.attachment_path)}
                          >
                            View
                          </Button>
                        )}
                      </td>
                      <td>{getStatusBadge(sub)}</td>
                      <td>
                        {sub.obtained_marks !== null ? (
                          <span className="fw-bold">
                            {sub.obtained_marks}/{assignment.total_marks}
                          </span>
                        ) : (
                          <span className="text-muted">Not graded</span>
                        )}
                      </td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleViewSubmission(sub)}
                          disabled={!sub.submitted_at}
                        >
                          <FaEye className="me-1" /> Grade
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {renderPagination()}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Grade Modal */}
      <Modal
        show={showGradeModal}
        onHide={() => setShowGradeModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Grade Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSubmission && (
            <>
              <div className="mb-3 p-3 bg-light rounded">
                <h6>Student: {selectedSubmission.student_name}</h6>
                <p>
                  <strong>Submitted at:</strong>{" "}
                  {formatDate(selectedSubmission.submitted_at)}
                </p>
                {selectedSubmission.submission_text && (
                  <>
                    <strong>Submission Text:</strong>
                    <p className="mt-1">{selectedSubmission.submission_text}</p>
                  </>
                )}
                {selectedSubmission.attachment_path && (
                  <div>
                    <strong>Attachment:</strong>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() =>
                        viewFile(selectedSubmission.attachment_path)
                      }
                    >
                      View File
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() =>
                        downloadFile(
                          selectedSubmission.attachment_path,
                          selectedSubmission.attachment_name,
                        )
                      }
                    >
                      Download
                    </Button>
                  </div>
                )}
              </div>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Obtained Marks <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max={assignment.total_marks}
                    value={obtainedMarks}
                    onChange={(e) => setObtainedMarks(e.target.value)}
                    placeholder={`Enter marks (0-${assignment.total_marks})`}
                  />
                  <Form.Text className="text-muted">
                    Maximum marks: {assignment.total_marks}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Feedback / Comments</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Provide feedback to the student..."
                  />
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowGradeModal(false)}
            disabled={gradingLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleGradeSubmit}
            disabled={gradingLoading}
          >
            {gradingLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving...
              </>
            ) : (
              <>
                <FaCheckCircle className="me-2" /> Save Grade
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AssignmentSubmissionsView;
